import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    CampaignFilterType,
    DateFilterType,
    Field,
    Filter,
    GroupField,
    isEmptinessFilterType,
    ListFilterType,
    NewsletterFilterType,
    NumberFilterType,
    StringFilterType
} from "../../filter.type";
import { TranslateService } from '@ngx-translate/core';
import {MessageService, TreeNode} from "primeng/api";
import {Dropdown} from "primeng/dropdown";
import {FilterPresetService} from "../../filter-preset/filter-preset.service";

type CurrentValue = string | number | boolean | Date | [Date, Date];

@Component({
    selector: 'dmp-filter-edit',
    templateUrl: './filter-edit.component.html',
    styleUrls: ['./filter-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [MessageService],
    standalone: false
})
export class FilterEditComponent implements OnInit {
    @Input() fields!: Field[] | GroupField[];
    @Input() rowNode!: TreeNode;

    @Input() filter: Filter = null;
    @Output() filterChange = new EventEmitter<Filter>();
    @Output() onDelete = new EventEmitter<Filter>();
    @Input() tenant!: string;
    @Input() partner: string;
    @Input() commandTable!: string;
    @Input() newsletterTable: boolean = true;

    @ViewChild('container') container: ElementRef;
    @ViewChild('fieldSelect', {static: true}) fieldSelect: Dropdown;

    public string_type = Object.values(StringFilterType).map((value) => ({code: value, value}));
    public number_type = Object.values(NumberFilterType).map((value) => ({code: value, value}));
    public date_type = Object.values(DateFilterType).map((value) => ({code: value, value}));
    public list_type = Object.values(ListFilterType).map((value) => ({code: value, value}));
    public newsletter_type = Object.values(NewsletterFilterType).map((value) => ({code: value, value}));
    public campaign_status_type = [
        { code: 'DRAFT',       value: this.translate.instant('campaign.status.DRAFT') },
        { code: 'IN_PROGRESS', value: this.translate.instant('campaign.status.IN_PROGRESS') },
        { code: 'SUSPENDED',   value: this.translate.instant('campaign.status.SUSPENDED') },
        { code: 'TERMINATED',  value: this.translate.instant('campaign.status.TERMINATED') },
        { code: 'ARCHIVED',    value: this.translate.instant('campaign.status.ARCHIVED') },
        { code: 'DELETED',     value: this.translate.instant('campaign.status.DELETED') }
    ];

    public campaign_association_status_type = [
        { code: 'NEW_CONTACT',      value: this.translate.instant('campaign.associationStatus.NEW_CONTACT') },
        { code: 'EXISTING_CONTACT', value: this.translate.instant('campaign.associationStatus.EXISTING_CONTACT') }
    ];
    public campaign_type = Object.values(CampaignFilterType).map((value) => ({code: value, value}));


    readonly isEmptinessFilterType = isEmptinessFilterType;
    readonly dateRange = DateFilterType.RANGE;
    readonly dateExact = DateFilterType.EXACTLY;
    readonly dateSlidingRange = DateFilterType.SLIDING_RANGE;
    readonly newsletterSubscribedTo = NewsletterFilterType.SUBSCRIBED_TO;

    readonly defaultValues = {
        string: StringFilterType.CONTAINS,
        number: NumberFilterType.EQUALS,
        date: DateFilterType.RANGE,
        list: ListFilterType.EXACTLY,
        newsletter: NewsletterFilterType.SUBSCRIBED_TO
    };

    constructor(private cdr: ChangeDetectorRef,
                private messageService: MessageService,
                private filterService: FilterPresetService,
                private translate: TranslateService) {
    }

    ngOnInit(): void {
    }

    public changeFieldFilter(rowNode: any, field: any): void {
        console.log('changeFieldFilter');
        this.clearValue();
        if (field) {
            if (field.field.variableType === 'date') {
                this.filter.options = {excludeYear: false};
                this.filter.filterType = {
                    code: this.defaultValues.date,
                    value: this.defaultValues.date
                };
            } else if (field.field.variableType === 'campaign') {
                this.filter.options = this.filter.options || {};
                    if (field.field.name === '_campaign_flux') {
                    // Type flux import / export
                    this.filter.filterType = {
                        code: CampaignFilterType.IMPORT,
                        value: CampaignFilterType.IMPORT
                    } as any;
                } else if (field.field.name === '_campaign_status') {
                    // Statut : simple dropdown, on n'utilise pas filterType
                    this.filter.filterType = null;
                    this.filter.options.campaignStatus = null;
                    } else if (field.field.name === '_campaign_contact_status') {
                        // Statut de contact à l'association : simple dropdown, sans filterType
                        this.filter.filterType = null;
                        this.filter.options.associationStatus = null;
                } else {
                    // Autres champs campagne (code, name, utm*) : comportement string
                    this.filter.filterType = {
                        code: this.defaultValues.string,
                        value: this.defaultValues.string
                    };
                }
            } else {
                this.filter.options = {};
                this.filter.filterType = {
                    code: this.defaultValues[field.field.variableType],
                    value: this.defaultValues[field.field.variableType]
                };
            }
        } else {
            this.filter.filterType = null;
        }
    }

    doValid(rowData: any) {
        this.filter.edit = false;
        console.log(this.filter);
        this.filterChange.emit(this.filter);
    }

    clearValue() {
        this.filter.value = null;
    }

    validateNumber($event: any, chips: any) {
        console.log($event);
        const value = $event.value;
        if (isNaN(parseFloat(value))) {
            chips.value = chips.value.filter(i => i !== value);
            this.messageService.add({severity: 'error', summary: this.translate.instant('components.filter-edit.invalidValueSummary'), detail: this.translate.instant('components.filter-edit.invalidValueDetail')});
        }

    }

    checkTypeChips(code?: string) {
        if (code) {
            return ![NumberFilterType.GREATER, NumberFilterType.GREATER_EQUALS,
                NumberFilterType.LESS_EQUALS, NumberFilterType.LESS, NumberFilterType.RANGE]
                .map(v => v.toString())
                .includes(code);
        } else {
            return false;
        }
    }

    checkTypeRange(code?: string) {
        if (code) {
            return [NumberFilterType.RANGE]
                .map(v => v.toString())
                .includes(code);
        } else {
            return false;
        }
    }

    public doUpdateTypeFilter(filter: Filter): void {
        if (filter.filterType.code === NumberFilterType.RANGE.toString()) {
            filter.value = [null, null];
        } else {
            filter.value = null;
        }
    }

    public doUpdateDateTypeFilter(): void {
        this.filter.value = null;
        // « Est vide » / « N'est pas vide » ne comparent aucune date : l'option sans l'année est masquée et désactivée
        if (isEmptinessFilterType(this.filter.filterType?.code)) {
            this.filter.options = {...this.filter.options, excludeYear: false};
        }
    }

    public deleteFilter(): void {
        this.onDelete.emit(this.filter);
    }

    public doAddFilter(operator: "OR" | "AND") {
        this.filterService.addGroupBlankFilter(this.rowNode, operator);
    }

    /**
     * Check if the current filter is using HOUR type in période glissante
     * When HOUR is selected, the excludeYear checkbox should be disabled
     */
    public isHourTypeFilter(): boolean {
        if (this.filter?.filterType?.code === DateFilterType.SLIDING_RANGE &&
            Array.isArray(this.filter.value) &&
            this.filter.value.length > 0) {
            return this.filter.value[0] === 'HOUR';
        }
        return false;
    }

    public showNewsletterList() {
        return this.filter.filterType.code === NewsletterFilterType.SUBSCRIBED_TO ||
            this.filter.filterType.code === NewsletterFilterType.NOT_SUBSCRIBED_TO;
    }
}
